import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Radio, Calendar, Users, ClipboardList, BarChart3, FileText, Play, TrendingUp, TrendingDown, Minus,
  Trophy, Heart, UserPlus, Bell, ShieldAlert, Zap, ArrowRight, Crown, Share2, Download, Copy, X,
} from "lucide-react";

const FOUNDING_BADGE = "https://static.prod-images.emergentagent.com/jobs/9b720c6b-a18d-41be-8fe8-54fcc8746426/images/9ac323128e970e04ef89bdc335e78351df095dc61985c6257e31aa93645e9ee4.png";

export default function CommandCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [welcome, setWelcome] = useState(false);

  useEffect(() => {
    api.get("/live/command-center").then(({ data }) => {
      setD(data);
      if (data.founding?.is_founding_member && !data.founding?.welcome_seen) setWelcome(true);
    }).catch(() => setD({ is_staff: false }));
  }, []);

  const dismissWelcome = () => { setWelcome(false); api.post("/live/founding-welcome-seen").catch(() => {}); };

  if (!d) return <div className="min-h-screen bg-[#F1F3F6]"><Header /><div className="text-center text-slate-400 py-24">Loading your Command Center…</div></div>;

  return (
    <div className="min-h-screen bg-[#F1F3F6]"><Header />
      {welcome && <FoundingWelcome org={d.founding?.org} onClose={dismissWelcome} />}
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="label-overline text-[#E63946] flex items-center gap-2"><Radio className="w-4 h-4" /> Baseball Command Center</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-1">Good day{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
          <p className="text-sm text-white/60 mt-1">Everything you need today, in one place.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="command-center">
        {!d.is_staff ? (
          <Widget title="No teams yet" testid="cc-empty">
            <p className="text-sm text-slate-500">You're not assigned to any teams yet. Claim your organization or ask an owner to invite you.</p>
            <Link to="/live/claim" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-[#E63946]">Claim an organization <ArrowRight className="w-4 h-4" /></Link>
          </Widget>
        ) : (
          <div className="grid lg:grid-cols-3 gap-5">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-5">
              <TodaysGames games={d.today_games} onStart={(g) => navigate(`/live/games/${g.id}`)} />
              <MyTeams teams={d.my_teams} />
              <OrgActivity items={d.org_activity} />
            </div>
            {/* Right column */}
            <div className="space-y-5">
              <QuickActions teams={d.my_teams} navigate={navigate} />
              <TeamStatus s={d.team_status} />
              <PitchAlerts alerts={d.pitch_alerts} />
              <RankingsWidget rows={d.rankings} />
              <UpcomingEvents events={d.upcoming_events} />
              <NotificationsWidget items={d.notifications} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- widget shell ---------- */
function Widget({ title, icon: Icon, children, testid, action }) {
  return (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#E63946]" />} {title}</div>
        {action}
      </div>
      {children}
    </section>
  );
}
const Trend = ({ t }) => t === "up" ? <TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> : t === "down" ? <TrendingDown className="w-3.5 h-3.5 text-[#E63946]" /> : <Minus className="w-3.5 h-3.5 text-slate-300" />;

/* ---------- widgets ---------- */
function TodaysGames({ games, onStart }) {
  return (
    <Widget title="Today's Games" icon={Calendar} testid="widget-todays-games">
      {games.length === 0 ? <p className="text-sm text-slate-400">No games scheduled for today.</p> : (
        <div className="space-y-3">
          {games.map((g) => (
            <div key={g.id} data-testid={`today-game-${g.id}`} className="flex items-center gap-4 p-3 rounded-sm bg-slate-50">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{g.team_name} <span className="text-slate-400">vs</span> {g.opponent}</div>
                <div className="text-[11px] text-slate-400 capitalize">{g.time || "TBD"}{g.location ? ` · ${g.location}` : ""} · {(g.game_type || "").replace("_", " ")} · {g.status}</div>
              </div>
              {g.startable
                ? <button data-testid={`start-game-${g.id}`} onClick={() => onStart(g)} className="flex items-center gap-1.5 bg-[#E63946] text-white text-sm font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:bg-[#c62f3b]"><Play className="w-4 h-4" /> Start Game</button>
                : <Link to={`/live/games/${g.id}`} className="text-xs font-semibold text-[#0A192F] border border-slate-200 px-4 py-2 rounded-sm">View</Link>}
            </div>
          ))}
        </div>
      )}
    </Widget>
  );
}

function MyTeams({ teams }) {
  const actions = (t) => [
    ["Profile", `/live/teams/${t.slug}`, FileText],
    ["Schedule", `/live/teams/${t.slug}`, Calendar],
    ["Roster", `/live/teams/${t.slug}`, Users],
    ["Stats", `/live/teams/${t.slug}`, BarChart3],
    ["Portfolio", `/live/teams/${t.slug}/portfolio`, Trophy],
  ];
  return (
    <Widget title="My Teams" icon={Users} testid="widget-my-teams">
      {teams.length === 0 ? <p className="text-sm text-slate-400">No teams assigned.</p> : (
        <div className="space-y-3">
          {teams.map((t) => (
            <div key={t.id} data-testid={`my-team-${t.id}`} className="border border-slate-100 rounded-sm p-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-[#0A192F] grid place-items-center overflow-hidden shrink-0">
                  {t.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-white/50" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[11px] text-slate-400">{t.record.w}-{t.record.l} · {t.roster_count} players{t.rating.national_rank ? ` · #${t.rating.national_rank} Nat'l` : ""}</div>
                </div>
                <span className="flex items-center gap-1 text-[11px] text-slate-400"><Trend t={t.rating.trend} /></span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {actions(t).map(([label, to, Icon]) => (
                  <Link key={label} to={to} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#E63946] border border-slate-100 rounded-full px-2.5 py-1"><Icon className="w-3 h-3" /> {label}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Widget>
  );
}

function TeamStatus({ s }) {
  const cells = [["Available", s.available, "text-emerald-600"], ["Injured", s.injured, "text-[#E63946]"], ["Away", s.away, "text-amber-600"], ["Pending", s.pending_invitations, "text-slate-500"]];
  return (
    <Widget title="Team Status" icon={ClipboardList} testid="widget-team-status">
      <div className="grid grid-cols-2 gap-3">
        {cells.map(([k, v, c]) => <div key={k} className="bg-slate-50 rounded-sm py-3 text-center"><div className={`font-data text-2xl ${c}`}>{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div></div>)}
      </div>
    </Widget>
  );
}

function PitchAlerts({ alerts }) {
  const a = alerts || {};
  const resting = a.resting || [];
  const approaching = a.approaching || [];
  const empty = !resting.length && !approaching.length;
  return (
    <Widget title="Pitch Count Alerts" icon={ShieldAlert} testid="widget-pitch-alerts">
      {a.enabled === false ? (
        <p className="text-sm text-slate-400">Set age-division pitch-count rules in admin to enable arm-safety alerts.</p>
      ) : empty ? (
        <p className="text-sm text-slate-400" data-testid="pitch-alerts-empty">All arms healthy — {a.available_count ?? 0} pitcher{(a.available_count ?? 0) !== 1 ? "s" : ""} available today.</p>
      ) : (
        <div className="space-y-3">
          {approaching.length > 0 && (
            <div data-testid="pitch-approaching">
              <div className="label-overline text-[10px] text-[#E63946] mb-1.5">Approaching / At Limit</div>
              <ul className="space-y-1.5">
                {approaching.map((p, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate"><span className="font-semibold">{p.name}</span> <span className="text-slate-400 text-xs">· {p.team}</span></span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0 ${p.level === "critical" ? "bg-[#E63946] text-white" : "bg-amber-100 text-amber-700"}`}>{p.pitches}/{p.daily_max}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {resting.length > 0 && (
            <div data-testid="pitch-resting">
              <div className="label-overline text-[10px] mb-1.5">Requiring Rest</div>
              <ul className="space-y-1.5">
                {resting.map((p, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate"><span className="font-semibold">{p.name}</span> <span className="text-slate-400 text-xs">· {p.team}</span></span>
                    <span className="text-[11px] text-slate-500 shrink-0">rest {p.days_until_eligible}d · until {p.eligible_date}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">{a.available_count ?? 0} pitcher{(a.available_count ?? 0) !== 1 ? "s" : ""} available today</div>
        </div>
      )}
    </Widget>
  );
}

function RankingsWidget({ rows }) {
  return (
    <Widget title="Rankings" icon={BarChart3} testid="widget-rankings">
      {rows.length === 0 ? <p className="text-sm text-slate-400">No ranked teams yet.</p> : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-sm" data-testid={`ranking-${i}`}>
              <span className="flex-1 truncate font-semibold">{r.team}</span>
              <span className="text-[11px] text-slate-400">{r.national_rank ? `#${r.national_rank} Nat'l` : "Unranked"}{r.provincial_rank ? ` · #${r.provincial_rank} Prov` : ""}</span>
              <span className="font-data text-slate-600">{r.rating ?? "—"}</span>
              <Trend t={r.trend} />
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function OrgActivity({ items }) {
  const icon = { award: Trophy, follow: Heart, commitment: UserPlus };
  return (
    <Widget title="Organization Activity" icon={Zap} testid="widget-org-activity">
      {items.length === 0 ? <p className="text-sm text-slate-400">No recent activity.</p> : (
        <ul className="space-y-2.5">
          {items.map((a, i) => { const I = icon[a.type] || Zap; return (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <I className="w-4 h-4 text-[#E63946] mt-0.5 shrink-0" />
              <span className="flex-1"><span className="font-semibold">{a.label}</span>{a.team ? <span className="text-slate-400"> · {a.team}</span> : ""}</span>
              {a.date && <span className="text-[10px] text-slate-300">{new Date(a.date).toLocaleDateString()}</span>}
            </li>
          ); })}
        </ul>
      )}
    </Widget>
  );
}

function UpcomingEvents({ events }) {
  return (
    <Widget title="Upcoming Events" icon={Calendar} testid="widget-upcoming">
      {events.length === 0 ? <p className="text-sm text-slate-400">Nothing scheduled.</p> : (
        <ul className="space-y-2">
          {events.map((g) => (
            <li key={g.id} className="flex items-center gap-2 text-sm">
              <span className="text-[10px] font-bold uppercase text-[#E63946] w-10 shrink-0">{g.kind === "tournament" ? "TRNY" : "GAME"}</span>
              <Link to={`/live/games/${g.id}`} className="flex-1 truncate hover:text-[#E63946]">{g.team_name} vs {g.opponent}</Link>
              <span className="text-[11px] text-slate-400">{(g.date || "").slice(5)}</span>
            </li>
          ))}
        </ul>
      )}
    </Widget>
  );
}

function NotificationsWidget({ items }) {
  return (
    <Widget title="Notifications" icon={Bell} testid="widget-notifications">
      <p className="text-sm text-slate-400">You're all caught up. Messages, invitations and recruiter interest will appear here.</p>
    </Widget>
  );
}

function QuickActions({ teams, navigate }) {
  const first = teams[0];
  const btns = [
    ["Score Game", () => navigate("/admin/live/games"), Play],
    ["Schedule", () => first && navigate(`/live/teams/${first.slug}`), Calendar],
    ["Add Player", () => first && navigate(`/live/teams/${first.slug}`), UserPlus],
    ["Invite Coach", () => first && navigate(`/live/teams/${first.slug}`), Users],
    ["Rankings", () => navigate("/live/team-rankings"), BarChart3],
    ["Membership", () => navigate("/membership"), Crown],
  ];
  return (
    <Widget title="Quick Actions" icon={Zap} testid="widget-quick-actions">
      <div className="grid grid-cols-2 gap-2">
        {btns.map(([label, fn, Icon]) => (
          <button key={label} data-testid={`qa-${label.toLowerCase().replace(/\s+/g, "-")}`} onClick={fn}
            className="flex items-center gap-2 bg-slate-50 hover:bg-[#0A192F] hover:text-white text-[#0A192F] text-xs font-semibold uppercase tracking-wider px-3 py-2.5 rounded-sm transition-colors">
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
    </Widget>
  );
}

/* ---------- founding member one-time welcome ---------- */
function FoundingWelcome({ org, onClose }) {
  const link = org?.slug ? `${window.location.origin}/live/orgs/${org.slug}` : window.location.origin;
  const share = async () => {
    const data = { title: "CBR Founding Member", text: `${org?.name || "We're"} a Founding Member of CBR Live!`, url: link };
    if (navigator.share) { try { await navigator.share(data); } catch (e) { /* cancelled */ } }
    else { navigator.clipboard.writeText(link); toast.success("Link copied"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="founding-welcome-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-8 text-center relative">
        <button data-testid="founding-welcome-close" onClick={onClose} className="absolute top-3 right-3 text-slate-300 hover:text-slate-500"><X className="w-5 h-5" /></button>
        <img src={FOUNDING_BADGE} alt="Founding Member" className="w-28 h-28 mx-auto" data-testid="founding-badge-img" />
        <h2 className="font-display text-2xl uppercase tracking-tight mt-3">🎉 Congratulations!</h2>
        <p className="text-sm text-slate-600 mt-2">You're one of the founding organizations helping build the future of Canadian baseball. As a <b>Founding Member</b>, you'll permanently keep the badge and get early access to new CBR Live features.</p>
        <div className="flex gap-2 mt-5">
          <button data-testid="founding-share-btn" onClick={share} className="flex-1 flex items-center justify-center gap-1.5 bg-[#E63946] text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm"><Share2 className="w-4 h-4" /> Share</button>
          <a data-testid="founding-download-btn" href={FOUNDING_BADGE} download="cbr-founding-member.png" target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 border border-slate-200 text-[#0A192F] text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm"><Download className="w-4 h-4" /> Download</a>
          <button data-testid="founding-copy-btn" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} className="flex-1 flex items-center justify-center gap-1.5 border border-slate-200 text-[#0A192F] text-xs font-semibold uppercase tracking-wider py-2.5 rounded-sm"><Copy className="w-4 h-4" /> Copy Link</button>
        </div>
      </div>
    </div>
  );
}
